/* eslint-disable complexity */
// import classNames from 'classnames';
import { useState, useContext } from 'preact/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styleSheet from './styles/scorePredictor.module.scss';
import { useTranslation } from 'react-i18next';

import {
    isPredictionCorrect,
    getVotePercentage
} from '../helpers/scorePredictor.helpers';

import EditIcon from '../../assets/icons/EditIcon';

import SubmitButton from '../../components/shared-components/button/SubmitButton';
import ResultsBadge from './resultsBadge.component';

import { ScorePredictorContext } from './ScorePredictorContent.component';

import ScorePicker from './scorePicker.component';
import { getImageUrl, onErrorImage } from '../../helpers/images';

const styles = styleSheet.locals || {};

const TEAM_IMAGE_WIDTH = 288;
const MAX_SCORE = 999;

/**
 * Component for the PredictScreen
 *
 * @param {object} props prediction option props
 * @param {object} props.scorePredictor scorePredictor option data
 * @param {object} props.fanPrediction fan's prediction data
 * @param {Function} props.onEdit callback when user clicks edit button
 * @returns {Function} <ScorePredictorHeader />
 */
function PredictScreen({ scorePredictor, fanPrediction, onEdit }) {
    const { t } = useTranslation();
    const { submitPrediction, submittingPrediction } = useContext(
        ScorePredictorContext
    );

    const totalPredictions = scorePredictor?.total_submissions || 0;

    const isOpen = scorePredictor.state === 'open';
    const isClosed = scorePredictor.state === 'closed';
    const isResults = scorePredictor.state === 'resolved';

    const resultsNoPrediction = isResults && !fanPrediction;

    const showHelper = (isOpen || isClosed) && fanPrediction;

    const [scoreAlpha, setScoreAlpha] = useState(
        fanPrediction?.entity_a_score ?? 0
    );
    // State for Team Beta's score
    const [scoreBeta, setScoreBeta] = useState(
        fanPrediction?.entity_b_score ?? 0
    );

    // Use small font if either score is 3 digits
    const useSmallFont =
        scoreAlpha.toString().length > 2 || scoreBeta.toString().length > 2;

    // Function to increment Team Alpha's score, ensuring it doesn't exceed 999.
    const incrementScoreAlpha = () => {
        setScoreAlpha((prevScore) => Math.min(prevScore + 1, MAX_SCORE));
    };

    // Function to decrement Team Alpha's score, ensuring it doesn't go below 0.
    const decrementScoreAlpha = () => {
        setScoreAlpha((prevScore) => Math.max(prevScore - 1, 0));
    };

    // Function to increment Team Beta's score, ensuring it doesn't exceed 999.
    const incrementScoreBeta = () => {
        setScoreBeta((prevScore) => Math.min(prevScore + 1, MAX_SCORE));
    };

    // Function to decrement Team Beta's score, ensuring it doesn't go below 0.
    const decrementScoreBeta = () => {
        setScoreBeta((prevScore) => Math.max(prevScore - 1, 0));
    };

    const onSubmit = () => {
        const userPrediction = {
            entity_a_score: scoreAlpha,
            entity_b_score: scoreBeta
        };

        submitPrediction(userPrediction);
    };

    const onEditClick = () => {
        if (onEdit) {
            onEdit();
        }
    };

    const correctPredictions =
        scorePredictor?.questions?.[0]?.correct_options?.[0]?.times_selected;

    const numberOfCorrectFans = correctPredictions || 0;
    const totalFans = totalPredictions || 0;

    const isFanCorrect = isPredictionCorrect(scorePredictor, fanPrediction);

    /**
     * Renders the team image block for score predictor teams.
     *
     * @param {boolean} image - Whether the team has an image (truthy value).
     * @param {string} imageUrl - The URL for the standard team image.
     * @param {string} externalImageUrl - The URL for an external team image.
     * @param {string} imageText - Alt text for the image.
     * @param {string} imgClass - CSS class for the image wrapper div.
     * @returns {object|null} The image block or null if no image.
     */
    function renderTeamImage(
        image,
        imageUrl,
        externalImageUrl,
        imageText,
        imgClass
    ) {
        if (!image && !externalImageUrl) {
            return null;
        }

        if (imageUrl) {
            return (
                <div className={imgClass}>
                    <img
                        part="score-predictor-team-image"
                        src={getImageUrl(imageUrl, TEAM_IMAGE_WIDTH)}
                        alt={imageText || ''}
                        className={styles.scorePredictorTeamImage}
                    />
                </div>
            );
        }
        if (externalImageUrl) {
            return (
                <div className={imgClass} data-testid="external-team-image">
                    <img
                        part="score-predictor-team-image"
                        src={externalImageUrl}
                        alt={imageText || ''}
                        className={styles.scorePredictorTeamImage}
                        onError={onErrorImage}
                    />
                </div>
            );
        }
        return null;
    }

    if (resultsNoPrediction) {
        return (
            <div className={styles.scorePredictorResultsBadgeMobile}>
                <ResultsBadge
                    correctFans={numberOfCorrectFans}
                    totalFans={totalFans}
                    isFanCorrect={fanPrediction ? isFanCorrect : null}
                />
            </div>
        );
    }

    return (
        <div>
            <div
                part="score-predictor-predict-wrapper"
                className={classNames(styles.scorePredictorPredictWrapper, {
                    [styles.scorePredictorPredictWrapperHighlight]:
                        fanPrediction
                })}
            >
                {fanPrediction && (
                    <div
                        className={classNames(
                            styles.scorePredictorYourPredictionWrapper,
                            {
                                [styles.scorePredictorYourPredictionWrapperCenter]:
                                    isClosed || isResults
                            }
                        )}
                    >
                        <div
                            className={styles.scorePredictorYourPredictionText}
                        >
                            {t('scorePredictor.yourPredictionLabel')}
                        </div>

                        {isOpen && (
                            <button
                                type="button"
                                className={styles.scorePredictorEditButton}
                                aria-label="Edit your score prediction"
                                onClick={onEditClick}
                                data-testid="edit-prediction-button"
                            >
                                {t('button.edit')}
                                <span className={styles.scorePredictorEditIcon}>
                                    <EditIcon />
                                </span>
                            </button>
                        )}
                    </div>
                )}

                <div className={styles.scorePredictorPredictScreen}>
                    <div
                        part="score-predictor-team score-predictor-team-a"
                        className={classNames(styles.scorePredictorTeamA, {
                            [styles.scorePredictorTeamAPredicted]: fanPrediction
                        })}
                    >
                        {renderTeamImage(
                            scorePredictor?.entity_a_image,
                            scorePredictor?.entity_a_image_url,
                            scorePredictor?.entity_a_external_image_url,
                            scorePredictor?.entity_a_image_text,
                            classNames(styles.scorePredictorTeamAImg, {
                                [styles.scorePredictorTeamAImgPredicted]:
                                    fanPrediction
                            })
                        )}

                        <div
                            part="score-predictor-team-name"
                            className={styles.scorePredictorTeamNameA}
                        >
                            {scorePredictor?.entity_a_name}
                        </div>

                        {fanPrediction ? (
                            <div className={styles.scorePredictorFanScore}>
                                {fanPrediction.entity_a_score}
                            </div>
                        ) : (
                            <div className={styles.scorePredictorScorePickerA}>
                                <ScorePicker
                                    teamName={scorePredictor?.entity_a_name}
                                    score={scoreAlpha}
                                    onIncrement={incrementScoreAlpha}
                                    onDecrement={decrementScoreAlpha}
                                    onChange={setScoreAlpha}
                                    useSmallFont={useSmallFont}
                                />
                            </div>
                        )}
                    </div>

                    {!fanPrediction && (
                        <span className={styles.scorePredictorScoreDivider}>
                            -
                        </span>
                    )}

                    <div
                        part="score-predictor-team score-predictor-team-b"
                        className={classNames(styles.scorePredictorTeamB, {
                            [styles.scorePredictorTeamBPredicted]: fanPrediction
                        })}
                    >
                        {renderTeamImage(
                            scorePredictor?.entity_b_image,
                            scorePredictor?.entity_b_image_url,
                            scorePredictor?.entity_b_external_image_url,
                            scorePredictor?.entity_b_image_text,
                            classNames(styles.scorePredictorTeamBImg, {
                                [styles.scorePredictorTeamBImgPredicted]:
                                    fanPrediction
                            })
                        )}

                        <div
                            part="score-predictor-team-name"
                            className={styles.scorePredictorTeamNameB}
                        >
                            {scorePredictor?.entity_b_name}
                        </div>

                        {fanPrediction ? (
                            <div className={styles.scorePredictorFanScore}>
                                {fanPrediction.entity_b_score}
                            </div>
                        ) : (
                            <div className={styles.scorePredictorScorePickerB}>
                                <ScorePicker
                                    teamName={scorePredictor?.entity_b_name}
                                    score={scoreBeta}
                                    onIncrement={incrementScoreBeta}
                                    onDecrement={decrementScoreBeta}
                                    onChange={setScoreBeta}
                                    useSmallFont={useSmallFont}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {fanPrediction && !isResults && (
                    <div className={styles.scorePredictorFanPercentage}>
                        {t('scorePredictor.fansAgree', {
                            percentage: getVotePercentage(
                                fanPrediction.times_selected,
                                totalPredictions
                            )
                        })}
                    </div>
                )}

                {isResults && (
                    <div className={styles.scorePredictorResultsBadge}>
                        <ResultsBadge
                            correctFans={numberOfCorrectFans}
                            totalFans={totalFans}
                            isFanCorrect={fanPrediction ? isFanCorrect : null}
                        />
                    </div>
                )}
            </div>

            {showHelper && (
                <div
                    className={styles.scorePredictorHelper}
                    data-testid="predict-come-back-later-text"
                >
                    {t('scorePredictor.comeBackLater')}
                </div>
            )}

            {!fanPrediction && isOpen && (
                <div className={styles.scorePredictorSubmitButton}>
                    <SubmitButton
                        onSubmit={onSubmit}
                        title="Submit your score prediction"
                        buttonText={t('scorePredictor.savePrediction')}
                        loading={submittingPrediction}
                    />
                </div>
            )}
        </div>
    );
}

PredictScreen.propTypes = {
    scorePredictor: PropTypes.object.isRequired,
    fanPrediction: PropTypes.object.isRequired,
    onEdit: PropTypes.func
};

export default PredictScreen;
